/Sites/FCCO/src/components/NavbarElement.vue
<template>
    <!-- Navbar start -->
    <nav class="w-screen fixed bg-opacity-90 bg-white p-4 lg:px-20 flex justify-between z-50 transition-colors duration-300">
        <!-- Logo start -->
        <div class="h-full flex flex-row items-center">
            <template v-if="!isStorePage">
                <router-link to="/">
                    <img 
                        :src="require('@/assets/fcco.png')" 
                        alt="logo" 
                        style="max-width:100px;" 
                        class="logo transition-opacity duration-300"
                    />
                </router-link>
            </template>
            <template v-else>
                <button @click="goBackToHome" class="flex items-center text-black hover:text-gray-600 transition-colors">
                    <i class="fa-solid fa-arrow-left mr-2"></i>
                    <span>{{ $t('navbar.back') || 'Back' }}</span>
                </button>
            </template>        
        </div> 
        <!-- Logo end -->

        <!-- Toggler start -->
        <button v-if="!isStorePage" role="menubar" class="md:hidden text-black" @click="navToggle()">
            <i class="fa-solid fa-bars text-2xl"></i>
        </button>
        <!-- Toggler end -->

        <!-- Menu content start -->
        <div v-if="!isStorePage" class="absolute left-0 right-0 translate-y-16 bg-opacity-90 bg-white md:bg-none md:bg-opacity-0 shadow hidden md:flex flex-col gap-4 items-center p-4 md:flex-row md:static md:shadow-none md:translate-y-0 transition-colors duration-300" role="menu" aria-expanded="false">
            <!-- Links start -->
            <a href="https://facecageco.printify.me/" role="menuitem" target="_blank" class="text-black hover:text-gray-600 transition-colors">
                <p class="mx-4">{{ $t('navbar.shop') }}</p>
            </a>
           
            <router-link to="/lab" role="menuitem" class="text-black hover:text-gray-600 transition-colors">
                <p class="mx-4">{{ $t('navbar.lab') }}</p>
            </router-link>
            <router-link v-if="shouldShowVideosLink" to="/videos" role="menuitem" class="text-black hover:text-gray-600 transition-colors">
                <p class="mx-4">{{ $t('navbar.videos') }}</p>
            </router-link>
            <a href="https://www.youtube.com/@facecageco" role="menuitem" target="_blank" class="text-black hover:text-gray-600 transition-colors">
                <p class="mx-4">{{ $t('navbar.youtube') }}</p>
            </a>
            <a href="#" role="menuitem" v-if="isLive" class="text-red-500 font-bold">
                <p class="mx-4" style="border: 1px solid red">
                    {{ $t('navbar.live') }}
                    <span class="inline-block w-2 h-2 rounded-full bg-red-500 ml-1"></span>
                </p>
            </a>
            <!-- Links end -->

            <!-- Theme and Language controls -->
            <div class="md:ml-auto flex items-center">
                <!-- Language Switcher -->
                <language-switcher></language-switcher>
            </div>
        </div>
        <!-- Menu content end -->
    </nav>
    <!-- Navbar end -->
</template>

<script>
import axios from 'axios';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import { youtubeStatus } from '@/utils/youtube-status';

export default {
    components: {
        LanguageSwitcher
    },
    data() {
        return {
            isLive: false,
            forceLiveForTesting: false, 
            channelId: 'UCNVaroed2-3nyCCqiPJQ2mw', 
            checkInterval: null
        }
    },
    computed: {
        isStorePage() {
            return this.$route.path === '/store';
        },
        shouldShowVideosLink() {
            // Show videos link if there's no error or if we're currently loading
            return !youtubeStatus.hasError || youtubeStatus.isLoading;
        }
    },
    methods: {
        navigateToAbout() {
            if (this.$route.path === '/') {
                // Already on home page, just scroll to section
                document.getElementById('about-section').scrollIntoView({ 
                    behavior: 'smooth'
                });
            } else {
                // On another page, navigate to home page with about section as target
                this.$router.push({ 
                    path: '/',
                    // This will be handled in the home component's mounted hook
                    query: { scrollTo: 'about' }  
                });
            }
        },
        goBackToHome() {
            this.$router.push('/');
        },
        // Toggle navbar mode 
        navToggle() {
            let menu = document.querySelector("[role='menu']");
            let isExpanded = menu.getAttribute('aria-expanded');
            menu.setAttribute('aria-expanded', !isExpanded);
            menu.classList.toggle('hidden');
            menu.classList.toggle('flex');
        },
        // Check if YouTube channel is currently live
        async checkLiveStatus() {
            if (this.forceLiveForTesting) {
                this.isLive = true;
                return;
            }

            try {
                const API_KEY = process.env.VUE_APP_YOUTUBE_API_KEY;
                
                const response = await axios.get(`https://www.googleapis.com/youtube/v3/search`, {
                    params: {
                        part: 'snippet',
                        channelId: this.channelId,
                        eventType: 'live',
                        type: 'video',
                        key: API_KEY
                    }
                });
                
                this.isLive = response.data.items && response.data.items.length > 0;
            } catch (error) {
                console.error('Error checking live status:', error);
                this.isLive = false;
            }
        },
        // Quick check to see if YouTube API is working for videos link visibility
        async checkYouTubeAPIStatus() {
            // Only check if we don't already have status information
            if (youtubeStatus.lastChecked) {
                return; // Already checked, don't waste API quota
            }
            
            try {
                const API_KEY = process.env.VUE_APP_YOUTUBE_API_KEY;
                
                if (!API_KEY) {
                    youtubeStatus.setError('api_key_missing');
                    return;
                }
                
                // Simple API test with minimal quota usage
                const response = await axios.get(`https://www.googleapis.com/youtube/v3/channels`, {
                    params: {
                        part: 'snippet',
                        id: this.channelId,
                        key: API_KEY
                    }
                });
                
                if (response.data.items && response.data.items.length > 0) {
                    youtubeStatus.setSuccess();
                } else {
                    youtubeStatus.setError('channel');
                }
            } catch (error) {
                console.warn('YouTube API status check failed:', error);
                let errorType = 'network';
                if (error.response?.status === 403) {
                    errorType = 'quota';
                } else if (error.response?.status === 400) {
                    errorType = 'api';
                }
                youtubeStatus.setError(errorType);
            }
        }
    },
    mounted() {
        // Check live status only once on page load/refresh
        this.checkLiveStatus();
        
        // Initialize YouTube status check for videos link visibility
        this.checkYouTubeAPIStatus();
        
        // Note: Removed recurring interval to save API quota
        // Previously checked every 2 minutes (120000ms)
    },
    beforeUnmount() {
        // Clear interval when component is destroyed (no longer needed)
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
        }
    }
}
</script>